import LoadingButton from '@mui/lab/LoadingButton';
import {Autocomplete, Button, styled, TextField} from '@mui/material';
import Divider from '@mui/material/Divider';
import {Stack} from '@mui/system';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';
import {get, isEqual, omit, toNumber} from 'lodash';
import {FC, useEffect, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {useParams} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {UserAccountService} from '../../../../api/services/user-account.service';
import {CompanyDto, ContractDto} from '../../../../global/api';
import {usePostCompaniesFindByIdsQuerySuspense} from '../../../../global/services/companies';
import {usePostContractsFindByIdsQuerySuspense, usePutContractsMutation} from '../../../../global/services/contracts';
import {CONTRACT_EDIT_ROLES} from '../../../../shared/constants';
import {DATE_FORMAT_FNS} from '../../../../shared/constants/date-formats';
import {checkAuthorities} from '../../../../shared/helpers/checkAuthorities';
import {formatManagerFio} from '../../../../shared/helpers/formatManagerFio';
import {TypeAccountRoles} from '../../../../shared/types/user-account.types';
import {authState} from '../../../../store/auth/atoms';
import Status from '../../../../ui/status/Status';
import {generalInfoModalOpen, useContractGeneralInfoModalStore} from '../../stores/contract-general-info-modal.store';
import {ContractCompanyPersons} from './contract-company-persons';
import {ContractGeneralInfoArchiveManager} from './contract-general-info-archive-manager';
import {ContractGeneralInfoFieldLabel} from './contract-general-info-field-label';
import {ContractGeneralInfoModal} from './contract-general-info-modal';

interface FormValues extends Omit<ContractDto, 'startDate' | 'dueDate'> {
  startDate: Date | null;
  dueDate: Date | null;
  inn?: string;
  company?: CompanyDto;
}

function prepareInitialValues(contract?: ContractDto, company?: CompanyDto): FormValues | undefined {
  if (!contract) return undefined;
  return {
    ...contract,
    startDate: contract?.startDate ? parseISO(contract.startDate) : null,
    dueDate: contract?.dueDate ? parseISO(contract.dueDate) : null,
    inn: company?.inn ?? '',
    companyId: contract.companyId ?? '',
    company,
  };
}

export const ContractGeneralInfo: FC = () => {
  const {id} = useParams();
  const {user} = useRecoilValue(authState);
  const modalIsOpen = useContractGeneralInfoModalStore(({open}) => open);
  const {mutate: updateContract, loading} = usePutContractsMutation();
  const [managerOptions, setManagerOptions] = useState<ContractDto['managers']>([]);
  const [, setManagersLoading] = useState(false);
  const contractRes = usePostContractsFindByIdsQuerySuspense([toNumber(id)]);
  const contract = get(contractRes, 'data.0');
  const companyId = get(contract, 'companyId');
  const companyRes = usePostCompaniesFindByIdsQuerySuspense([...(companyId ? [companyId] : [])]);
  const company = get(companyRes, 'data.0');

  const isArchived = get(contract, 'archived', false);
  const statusCode = !isArchived ? get(contract, 'statusCode') : 'ARCHIVED';
  const statusBorder = isArchived || isEqual(get(contract, 'statusCode'), 'CREATED');
  const canEditContract = checkAuthorities(CONTRACT_EDIT_ROLES, [...(user?.roles || [])]);
  const canEditExecutor = checkAuthorities(
    [
      TypeAccountRoles.PROJECT_MANAGER,
      TypeAccountRoles.DIRECTOR,
      TypeAccountRoles.FUNCTIONAL_ADMIN,
      TypeAccountRoles.ADMIN,
      TypeAccountRoles.OWNER,
    ],
    [...(user?.roles || [])],
  );

  useEffect(() => {
    let active = true;
    const loadManagers = async () => {
      try {
        setManagersLoading(true);
        const res = await UserAccountService.getAll({
          spec: {
            roles: [TypeAccountRoles.MANAGER],
            isActive: true,
          },
        });
        if (!active) return;

        const mapped =
          res.data.content?.map((manager) => ({
            uid: manager.uid,
            userLogin: manager.userLogin,
            email: manager.email ?? '',
            firstName: manager.firstName,
            lastName: manager.lastName,
            patronymic: manager.patronymic,
            userFIO: formatManagerFio(manager),
          })) ?? [];

        const existing = contract?.managers ?? [];
        const merged = [...existing, ...mapped].filter((item, index, list) => {
          if (!item?.uid) return false;
          return list.findIndex((candidate) => candidate?.uid === item.uid) === index;
        });

        setManagerOptions(merged);
      } catch (err) {
        if (active) {
          setManagerOptions(contract?.managers ?? []);
        }
      } finally {
        if (active) {
          setManagersLoading(false);
        }
      }
    };

    loadManagers();

    return () => {
      active = false;
    };
  }, [contract?.managers]);

  const formApi = useForm<FormValues>({
    defaultValues: prepareInitialValues(contract, company),
  });

  const {control, formState} = formApi;
  const formCompanyId = formApi.getValues('company.id');

  const handleResetForm = () => {
    formApi.reset(prepareInitialValues(contract, company));
  };

  const handleSubmitForm = async (values: FormValues) => {
    if (!canEditContract) return;
    const {company, startDate, dueDate, companyId, managers, ...rest} = omit(values, ['inn']);
    const managerUids = (managers ?? contract?.managers ?? [])
      .map((manager) => manager.uid)
      .filter((uid): uid is string => Boolean(uid));

    await updateContract(
      [
        {managers: managerUids},
        {
          ...rest,
          companyId: company?.id ?? companyId,
          companyName: company?.fullCompanyName,
          startDate: startDate?.toISOString(),
          dueDate: dueDate?.toISOString(),
        },
      ],
      (res) => formApi.reset(prepareInitialValues(res.data, company)),
    );
    contractRes.refresh();
  };

  return (
    <FormProvider {...formApi}>
      <form onSubmit={formApi.handleSubmit(handleSubmitForm)}>
        <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={3} width="100%">
              <ContractGeneralInfoFieldLabel label="Номер договора" starter>
                <Controller
                  name="contractNumber"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <StyledField {...field} disabled={!canEditContract} error={!!error} helperText={error?.message} />
                  )}
                  rules={{required: 'Обязательно для заполнения'}}
                />
              </ContractGeneralInfoFieldLabel>

              <ContractGeneralInfoFieldLabel label="Дата заключения">
                <Controller
                  name="startDate"
                  control={control}
                  render={({field, fieldState: {error}}) => (
                    <StyledDatePicker
                      disabled={!canEditContract}
                      format={DATE_FORMAT_FNS}
                      {...field}
                      slotProps={{
                        textField: {
                          helperText: error?.message,
                          error: !!error,
                        },
                      }}
                    />
                  )}
                  rules={{required: 'Обязательно для заполнения'}}
                />
              </ContractGeneralInfoFieldLabel>

              <ContractGeneralInfoFieldLabel label="Дата окончания">
                <Controller
                  name="dueDate"
                  control={control}
                  render={({field}) => (
                    <StyledDatePicker disabled={!canEditContract} format={DATE_FORMAT_FNS} {...field} />
                  )}
                />
              </ContractGeneralInfoFieldLabel>
            </Stack>

            <ContractGeneralInfoFieldLabel label="Описание контракта*" starter>
              <Controller
                name="description"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <StyledField
                    {...field}
                    disabled={!canEditContract}
                    multiline
                    rows={4}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                rules={{required: true, maxLength: {value: 400, message: 'Максимум 400 символов'}}}
              />
            </ContractGeneralInfoFieldLabel>

            <ContractGeneralInfoFieldLabel label="Исполнитель" starter>
              <Controller
                name="executor"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <StyledField
                    {...field}
                    disabled={!canEditContract || !canEditExecutor}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </ContractGeneralInfoFieldLabel>

            <ContractGeneralInfoFieldLabel label="Менеджеры по контракту" starter>
              <Controller
                name="managers"
                control={control}
                render={({field: {value, onChange}}) => (
                  <Autocomplete
                    sx={{width: '100%'}}
                    multiple
                    disabled={!canEditContract}
                    options={managerOptions?.length ? managerOptions : (value ?? [])}
                    value={value ?? []}
                    disableCloseOnSelect
                    getOptionLabel={(option) => formatManagerFio(option)}
                    isOptionEqualToValue={(option, value) => option.uid === value.uid}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderTags={(selected) => selected.map(formatManagerFio).join(', ')}
                    renderInput={(params) => (
                      <StyledField
                        {...params}
                        sx={{'& .MuiOutlinedInput-root': {px: '14px'}}}
                        placeholder={(value ?? []).length ? '' : 'Выберите менеджеров'}
                        InputProps={params.InputProps}
                      />
                    )}
                  />
                )}
              />
            </ContractGeneralInfoFieldLabel>

            <ContractGeneralInfoFieldLabel label="Статус контракта" starter>
              <Status entity="contract" code={statusCode} border={statusBorder} />
            </ContractGeneralInfoFieldLabel>

            <ContractGeneralInfoFieldLabel label="Тип устройства" starter>
              <StyledValue>{get(contract, 'deviceType.name')}</StyledValue>
            </ContractGeneralInfoFieldLabel>

            <StyledDivider />

            <ContractGeneralInfoFieldLabel label="Заказчик" starter>
              <Controller
                name="company.fullCompanyName"
                control={control}
                render={({field}) => (
                  <StyledValue>
                    <StyledLinkButton disabled={!canEditContract} onClick={generalInfoModalOpen}>
                      {field.value}
                    </StyledLinkButton>
                  </StyledValue>
                )}
              />
            </ContractGeneralInfoFieldLabel>

            <ContractGeneralInfoFieldLabel label="ИНН" starter>
              <Controller
                name="company.inn"
                control={control}
                render={({field}) => <StyledValue disabled={!canEditContract}>{field.value}</StyledValue>}
              />
            </ContractGeneralInfoFieldLabel>
          </Stack>
        </LocalizationProvider>

        {formCompanyId && <ContractCompanyPersons companyId={formCompanyId} />}

        <StyledDivider />

        <Stack direction="row" justifyContent="space-between">
          <Button
            color="error"
            variant="outlined"
            disabled={!canEditContract || !formState.isDirty}
            onClick={handleResetForm}
          >
            Отменить
          </Button>
          <Stack direction="row" gap={2}>
            <ContractGeneralInfoArchiveManager contract={contract} disabled={!canEditContract} />
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={!canEditContract || !formState.isDirty}
              loading={loading}
            >
              Сохранить
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
      {modalIsOpen && canEditContract && <ContractGeneralInfoModal />}
    </FormProvider>
  );
};

const StyledValue = styled('span')<{disabled?: boolean}>(({disabled, theme}) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '40px',
  border: `1px solid #0000003A`,
  padding: '4px 14px',
  ...(disabled && {
    color: theme.palette.action.disabled,
  }),
}));

const StyledField = styled(TextField)({
  width: '100%',

  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: '100%',
  },
});

const StyledDatePicker = styled(DatePicker)({
  width: '100%',

  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: '100%',
  },
});

const StyledDivider = styled(Divider)(({theme}) => ({
  borderColor: 'transparent',
  height: theme.spacing(4),
}));

const StyledLinkButton = styled(Button)({
  textDecoration: 'underline',
  textUnderlineOffset: '4px',

  '&:hover': {
    textDecoration: 'underline',
  },
});
